import tippy, { followCursor, Instance, Props } from 'tippy.js'
import { Plugin } from '../entities/plugin'
import type { AbstractEdge, AbstractNode, GraphEvent } from '../types'
import type { Graph } from '../entities'

export interface PopperOptions extends Omit<Partial<Props>, 'trigger'> {
  itemTypes?: Array<'node' | 'edge' | 'canvas'>
  shouldShow?: (event: GraphEvent) => boolean
  getContent?(): HTMLDivElement
  onPopperShow?(props: {
    graph: Graph
    popper: Instance
    itemType: 'canvas' | 'node' | 'edge'
    item?: AbstractNode | AbstractEdge
    hide: () => void
    show: () => void
  }): void
  trigger?: Array<'click' | 'hover' | 'contextmenu'>
  hideOnLeave?: boolean
}

const createEventHandler =
  (
    targetTrigger: 'click' | 'hover' | 'contextmenu',
    popup: Instance,
    saveItem: (item?: AbstractEdge | AbstractNode) => void
  ) =>
  (graph: Graph, options: PopperOptions, event: GraphEvent) => {
    const { itemTypes, trigger, getContent, onPopperShow } = options
    let { shouldShow } = options
    const content = getContent?.()
    const { type, originalEvent } = event
    const mouseEvent = originalEvent as MouseEvent

    if (type == null) {
      return
    }

    if (shouldShow == null) {
      shouldShow = () => true
    }

    if (
      trigger?.includes(targetTrigger) &&
      itemTypes?.includes(type) &&
      shouldShow(event) &&
      content
    ) {
      mouseEvent.preventDefault()

      popup.setProps({
        getReferenceClientRect: () =>
          ({
            width: 0,
            height: 0,
            top: mouseEvent.clientY,
            bottom: mouseEvent.clientY,
            left: mouseEvent.clientX,
            right: mouseEvent.clientX,
          } as DOMRect),
      })
      saveItem(event.item)

      if (popup.props.content !== content) {
        popup.setContent(content)
      }

      if (!popup.state.isShown) {
        popup.show()

        onPopperShow?.({
          graph,
          popper: popup,
          show: popup.show,
          hide: popup.hide,
          itemType: type,
          item: event.item,
        })
      }
    }
  }

export const PopperPlugin = Plugin.create<
  PopperOptions,
  {
    item?: AbstractEdge | AbstractNode
    popup?: Instance
    hide?: () => void
    show?: () => void
  }
>({
  name: 'popper',
  addOptions() {
    return {
      offset: [4, 4],
      trigger: ['contextmenu'],
      itemTypes: ['node'],
      shouldShow: () => true,
      hideOnLeave: false,
    }
  },
  addStorage() {
    return {}
  },
  onAfterInit(event, graph) {
    const {
      getContent,
      itemTypes,
      shouldShow,
      trigger,
      onPopperShow,
      hideOnLeave,
      ...rest
    } = this.options

    const popup = tippy(graph.renderer.container, {
      placement: 'right-start',
      interactive: true,
      appendTo: () => window.document.body,
      ...rest,
      showOnCreate: false,
      trigger: 'manual',
      ...(rest.followCursor
        ? {
            followCursor: true,
            plugins: [followCursor],
          }
        : []),
    })

    this.setStorage({
      popup,
      hide: popup.hide,
      show: popup.show,
    })
  },
  onContextMenu(event, graph) {
    if (this.storage.popup) {
      createEventHandler(
        'contextmenu',
        this.storage.popup,
        (item?: AbstractEdge | AbstractNode) => {
          this.setStorage({
            item,
          })
        }
      )(graph, this.options, event)
    }
  },
  onClick(event, graph) {
    if (this.storage.popup) {
      createEventHandler(
        'click',
        this.storage.popup,
        (item?: AbstractEdge | AbstractNode) => {
          this.setStorage({
            item,
          })
        }
      )(graph, this.options, event)
    }
  },
  onMouseOver(event, graph) {
    if (this.storage.popup) {
      createEventHandler(
        'hover',
        this.storage.popup,
        (item?: AbstractEdge | AbstractNode) => {
          this.setStorage({
            item,
          })
        }
      )(graph, this.options, event)
    }
  },
  onNodeMouseOut(event) {
    if (this.options.hideOnLeave && event.item?.id === this.storage.item?.id) {
      this.storage.hide?.()
    }
  },
  onEdgeMouseOut(event) {
    if (this.options.hideOnLeave && event.item?.id === this.storage.item?.id) {
      this.storage.hide?.()
    }
  },
  onViewportChange() {
    this.storage.popup?.hide()
  },
})
